import React, { useState } from 'react';
import { X, QrCode, ArrowLeft, CheckCircle, AlertCircle, Clock, Loader } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

interface MercadoPagoCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  pedido: any;
  onPagamentoConfirmado: (status: string, detalhes: any) => void;
  dadosCarrinho?: {
    itens: any[];
    observacoes?: string;
    valorFrete: number;
    enderecoEntrega: any;
    usuario: any;
  };
}

const MercadoPagoCheckout: React.FC<MercadoPagoCheckoutProps> = ({
  isOpen,
  onClose,
  pedido,
  onPagamentoConfirmado,
  dadosCarrinho
}) => {
  const [metodoPagamento, setMetodoPagamento] = useState<'pix' | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrCodePix, setQrCodePix] = useState<string>('');
  const [codigoPix, setCodigoPix] = useState<string>('');
  const [statusPagamento, setStatusPagamento] = useState<string>('');
  const [erro, setErro] = useState<string>('');
  const [paymentId, setPaymentId] = useState<string>('');
  const [tentativasVerificacao, setTentativasVerificacao] = useState<number>(0);
  const [maxTentativas] = useState<number>(24); // 2 minutos (24 * 5 segundos)
  const [mercadopagoStatus, setMercadopagoStatus] = useState<any>(null);

  const copiarCodigoPix = async () => {
    if (codigoPix) {
      try {
        await navigator.clipboard.writeText(codigoPix);
        toast.success('Código PIX copiado!');
    } catch (error) {
        console.error('Erro ao copiar código PIX:', error);
        toast.error('Erro ao copiar código PIX');
      }
    }
  };

  // Verificar status do Mercado Pago quando o modal abrir
  React.useEffect(() => {
    if (isOpen && !mercadopagoStatus) {
      verificarStatusMercadoPago();
    }
  }, [isOpen]);

  const verificarStatusMercadoPago = async () => {
    try {
      const response = await api.get('/api/mercadopago/status');
      setMercadopagoStatus(response.data);
      console.log('📊 Status do Mercado Pago:', response.data);
    } catch (error) {
      console.error('❌ Erro ao verificar status do Mercado Pago:', error);
    }
  };

  const processarPagamentoPix = async () => {
    try {
      setLoading(true);
      setErro('');
      setTentativasVerificacao(0);
      
      toast.loading('Gerando QR Code PIX...', { id: 'pix-loading' });
      
      // Verificar se o pedido já tem um ID (já foi criado)
      let pedidoId = pedido.id;
      
      if (!pedidoId) {
      // Primeiro, criar o pedido no banco para obter o ID real
      console.log('📡 Criando pedido no banco...');
      
      // Preparar dados do pedido com os dados do carrinho
      const dadosPedido = {
        mesa: 'Entrega',
        tipo_pedido: 'entrega',
        itens: dadosCarrinho?.itens || [],
        observacoes: dadosCarrinho?.observacoes || '',
        dados_cliente: {
          nome: dadosCarrinho?.usuario?.nome?.split(' ')[0] || pedido.email_cliente?.split('@')[0] || 'Cliente',
          sobrenome: dadosCarrinho?.usuario?.nome?.split(' ').slice(1).join(' ') || '',
          telefone: dadosCarrinho?.usuario?.telefone || '',
          email: dadosCarrinho?.usuario?.email || pedido.email_cliente || 'cliente@exemplo.com',
          endereco: dadosCarrinho?.enderecoEntrega || {}
        },
        valor_frete: dadosCarrinho?.valorFrete || 0,
        metodo_pagamento: 'site',
        detalhes_pagamento: { metodo: 'mercadopago' }
      };
      
      console.log('📋 Dados do pedido preparados:', dadosPedido);
      
      const pedidoResponse = await api.post('/api/pedidos', dadosPedido);
        pedidoId = pedidoResponse.data.id;
      console.log('✅ Pedido criado com ID:', pedidoId);
      } else {
        console.log('✅ Usando pedido existente com ID:', pedidoId);
      }
      
      const dadosPagamento = {
        transaction_amount: pedido.total,
        description: `Pedido #${pedidoId}`,
        payer: {
          email: pedido.email_cliente || 'cliente@exemplo.com'
        },
        external_reference: pedidoId.toString()
      };

      const response = await api.post('/api/mercadopago/pagar-pix', { dadosPagamento });
      
      if (response.data.qr_code_base64) {
        setQrCodePix(response.data.qr_code_base64);
        setCodigoPix(response.data.qr_code || '');
        setPaymentId(response.data.payment_id);
        setStatusPagamento('pending');
        
        toast.success('QR Code gerado! Escaneie com seu app bancário.', { id: 'pix-loading' });
        
        // Verificar status do pagamento a cada 5 segundos
        const interval = setInterval(async () => {
          try {
            setTentativasVerificacao(prev => prev + 1);
            
            const statusResponse = await api.get(`/api/mercadopago/status/${response.data.payment_id}`);
            
            if (statusResponse.data.status === 'approved') {
              setStatusPagamento('approved');
              clearInterval(interval);
              
              // Atualizar status do pedido no banco
              try {
                await api.put(`/api/pedidos/${pedidoId}/status-pagamento`, {
                  status_pagamento: 'pago'
                });
                console.log('✅ Status do pedido atualizado para pago');
              } catch (error) {
                console.error('❌ Erro ao atualizar status do pedido:', error);
              }
              
              toast.success('🎉 Pagamento aprovado! Seu pedido foi confirmado.', { 
                id: 'pix-success',
                duration: 5000 
              });
              onPagamentoConfirmado('approved', response.data);
            } else if (statusResponse.data.status === 'rejected') {
              setStatusPagamento('rejected');
              clearInterval(interval);
              toast.error('❌ Pagamento rejeitado. Tente novamente.', { 
                id: 'pix-error',
                duration: 5000 
              });
            } else if (tentativasVerificacao >= maxTentativas) {
              clearInterval(interval);
              toast.error('⏰ Tempo esgotado. O pagamento não foi confirmado.', { 
                id: 'pix-timeout',
                duration: 5000 
              });
            }
          } catch (error) {
            console.error('Erro ao verificar status:', error);
            if (tentativasVerificacao >= maxTentativas) {
              clearInterval(interval);
              toast.error('Erro ao verificar pagamento. Tente novamente.', { 
                id: 'pix-error',
                duration: 5000 
              });
            }
          }
        }, 5000);
      }
    } catch (error) {
      console.error('Erro ao processar PIX:', error);
      setErro('Erro ao processar pagamento PIX');
      toast.error('Erro ao gerar QR Code PIX. Tente novamente.', { id: 'pix-loading' });
    } finally {
      setLoading(false);
    }
  };


  const handleVoltar = () => {
    setMetodoPagamento(null);
    setQrCodePix('');
    setStatusPagamento('');
    setErro('');
  };

  const handleClose = () => {
    handleVoltar();
    onClose();
  };

  if (!isOpen) return null;

  // Modal Principal - Escolha do Método
  if (!metodoPagamento) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-700" data-modal-content>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">Pagamento pelo Site</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-red-400 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6" data-modal-content>
            {/* Valor Total */}
            <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Valor Total:</span>
                <span className="text-2xl font-bold text-green-400">
                  R$ {pedido?.total?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            {/* Status do Mercado Pago */}
            {mercadopagoStatus && (
              <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${mercadopagoStatus.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-blue-400 text-sm font-medium">
                    Mercado Pago {mercadopagoStatus.tokenType === 'production' ? 'Produção' : mercadopagoStatus.tokenType === 'test' ? 'Teste' : 'Desconhecido'}
                  </span>
                </div>
                <div className="text-xs text-blue-300 space-y-1">
                  <div>Cartão: {mercadopagoStatus.cardEnabled ? '✅ Ativado' : '❌ Desativado'}</div>
                  <div>PIX: {mercadopagoStatus.pixEnabled ? '✅ Ativado' : '❌ Desativado'}</div>
                  {mercadopagoStatus.message && (
                    <div className="text-blue-200 mt-1">{mercadopagoStatus.message}</div>
                  )}
                </div>
              </div>
            )}

            {/* Informação sobre PIX */}
            <div className="mb-6">
              <div className="p-4 bg-green-900/20 border border-green-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <QrCode className="text-green-400" size={20} />
                  <span className="text-green-400 font-medium">Pagamento PIX pelo Site</span>
                </div>
                <p className="text-green-300 text-sm">
                  Pague instantaneamente com PIX. Escaneie o QR Code ou copie o código PIX.
                </p>
              </div>
            </div>

            {/* Botão para iniciar PIX */}
            <div className="text-center">
              <button
                onClick={() => setMetodoPagamento('pix')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-medium transition-colors flex items-center gap-3 mx-auto text-lg"
              >
                <QrCode size={24} />
                Pagar com PIX
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal PIX
  if (metodoPagamento === 'pix') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-700" data-modal-content>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleVoltar}
                className="text-gray-400 hover:text-blue-400 transition-colors p-1"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-xl font-semibold text-white">Pagamento PIX</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-red-400 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6" data-modal-content>
            {/* Valor Total */}
            <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Valor Total:</span>
                <span className="text-2xl font-bold text-green-400">
                  R$ {pedido?.total?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                <p className="text-gray-300">Processando pagamento...</p>
              </div>
            ) : qrCodePix ? (
              <div className="text-center">
                <h3 className="text-lg font-medium text-white mb-4">Escaneie o QR Code</h3>
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img 
                    src={`data:image/png;base64,${qrCodePix}`} 
                    alt="QR Code PIX" 
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Use o app do seu banco para escanear o QR Code
                </p>
                
                {/* Código PIX Copia e Cola */}
                {codigoPix && (
                  <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-300">Código PIX (Copia e Cola)</h4>
                      <button
                        onClick={copiarCodigoPix}
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                      >
                        Copiar
                      </button>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-700 rounded p-3">
                      <p className="text-xs text-gray-400 break-all font-mono">
                        {codigoPix}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Cole este código no seu app bancário
                    </p>
                  </div>
                )}
                
                {statusPagamento === 'pending' && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-center gap-2 text-yellow-400 mb-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400"></div>
                      <span className="font-medium">Aguardando confirmação...</span>
                    </div>
                    <p className="text-yellow-300 text-sm text-center">
                      Escaneie o QR Code com seu app bancário e confirme o pagamento
                    </p>
                    <div className="mt-3 bg-yellow-500/20 rounded-lg p-2">
                      <p className="text-yellow-200 text-xs text-center">
                        Tentativas: {tentativasVerificacao}/{maxTentativas} • 
                        Tempo restante: {Math.max(0, maxTentativas - tentativasVerificacao) * 5}s
                      </p>
                    </div>
                  </div>
                )}
                
                {statusPagamento === 'approved' && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
                      <CheckCircle size={20} />
                      <span className="font-bold">Pagamento Aprovado!</span>
                    </div>
                    <p className="text-green-300 text-sm text-center">
                      Seu pedido foi confirmado e está sendo preparado
                    </p>
                  </div>
                )}

                {statusPagamento === 'rejected' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
                      <AlertCircle size={20} />
                      <span className="font-bold">Pagamento Rejeitado</span>
                    </div>
                    <p className="text-red-300 text-sm text-center">
                      O pagamento não foi aprovado. Tente novamente ou escolha outra forma de pagamento.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <button
                  onClick={processarPagamentoPix}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    'Gerar QR Code PIX'
                  )}
                </button>
              </div>
            )}

            {/* Botão para tentar novamente em caso de erro */}
            {(statusPagamento === 'rejected' || erro) && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setStatusPagamento('');
                    setErro('');
                    setQrCodePix('');
                    setCodigoPix('');
                    setTentativasVerificacao(0);
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Tentar Novamente
                </button>
              </div>
            )}

            {erro && (
              <div className="mt-4 p-3 bg-red-900/20 border border-red-700 rounded-lg">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertCircle size={16} />
                  {erro}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }


  return null;
};

export default MercadoPagoCheckout;
